import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';

import {
    AddLink,
    Biotech,
    ColorLens,
    ContentCutOutlined,
    CropOutlined,
    DocumentScanner,
    Explore,
    FileDownload,
    ManageAccounts,
    NoteAdd,
    PostAdd,
    ReceiptLong,
    Settings,
    Theaters,
    TravelExplore,
    VideoCall,
    ArtTrack,
    AutoAwesome,
    ContentCut,
    FormatColorFillOutlined,
    GpsFixed,
    JoinLeft,
    Movie,
    PictureAsPdf,
    SportsMotorsports,
    Vaccines,
    VerticalSplit,
    ViewComfy,
} from '@mui/icons-material'


import './SidebarScroll.css'

import PermissionGuard from '../../contexts/PermissionContext';
import ProjectContext from '../../contexts/ProjectContext';
import FormContext from '../../contexts/FormContext';

import SideList from './SideList';
import SideSettings from './SideSettings';
import ConfirmNavigationDialog from '../dialog/ConfirmNavigationDialog';

export const itemIcons = {
    'Upload': <PostAdd />,
    'Linking': <AddLink />,
    'Export': <FileDownload />,
    'Explore': <Explore />,
    'Asset Tracker': <TravelExplore />,
    'Upload Media Data': <VideoCall />,
    'Manage LUT': <ColorLens />,
    'Manage EDL': <ContentCutOutlined />,
    'FDL Generate': <CropOutlined />,
    'Deliverable Configuration': <DocumentScanner />,
    'Illusion': <Theaters />,
    'Pipeline Lab': <Biotech />,
    'Receipt Overview': <ReceiptLong />,
    'Settings': <Settings />,
    'Sources Configuration': <NoteAdd />,
    'Account Management': <ManageAccounts />,
}

export default function SideItems(props) {
    // SIDEITEMS PROPS
    const { isOffscreen,
            autoToggle,
            dailies, setDailies,
            illusion, setIllusion,
            assets, setAssets,
            project, setProject,
            settings, setSettings
        } = props
    const projectContext = useContext(ProjectContext)
    const { hasUnsavedChanges, setHasUnsavedChanges } = useContext(FormContext)
    const [currentPage, setCurrentPage] = React.useState(window.location.pathname)
    const navigate = useNavigate()

    // confirmation dialog
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);

    // SIDEITEMS VARIABLES  
    
    const dailiesMenu = () => { setDailies(!dailies) }
    const assetsMenu = () => { setAssets(!assets) }
    const projectMenu = () => { setProject(!project) }
    const illusionMenu = () => { setIllusion(!illusion) }
    const settingsMenu = () => { setSettings(!settings) }


    const handleNavigation = (page) => {
        if (hasUnsavedChanges) {
            setPendingNavigation(page);
            setConfirmDialogOpen(true);
        } else {
            setCurrentPage(page);
            navigate(page);
            autoToggle();
        }
    };

    const handleConfirmNavigation = () => {
        setConfirmDialogOpen(false);
        if (pendingNavigation) {
            // Clear unsaved changes for current form
            setCurrentPage(pendingNavigation);
            navigate(pendingNavigation);
            autoToggle();
            setPendingNavigation(null);
            setHasUnsavedChanges(false)
        }
    };

    const handleCancelNavigation = () => {
        setConfirmDialogOpen(false);
        setPendingNavigation(null);
    };

    const changePageUpload = () => handleNavigation('/upload');
    const changePageLink = () => handleNavigation('/linking');
    const changePageExport = () => handleNavigation('/export');
    const changePageExplore = () => handleNavigation('/explore');
    const changePageAssettrack = () => handleNavigation('/asset');
    const changePageMediaData = () => handleNavigation('/uploadmedia');
    const changePageIllusionPulls = () => handleNavigation('/illusion');
    const changePageSettings = () => handleNavigation('/settings');
    const changePageKonsolConfig = () => handleNavigation('/konsolconfig')
    const changePageLut = () => handleNavigation('/lut');
    const changePageEdlManage = () => handleNavigation('/edl');
    const changePageFdl = () => handleNavigation('/fdl');
    const changePageConfig = () => handleNavigation('/deliverableconfig');
    const changePageSources = () => handleNavigation('/sourceconfig');
    const changePageTranskoder = () => handleNavigation('/transkoder');
    const changePagePipelineLab = () => handleNavigation('/pipelinelab');
    const changePageSQLTools = () => handleNavigation('/sqltools');
    const changePageTkReceipts = () => handleNavigation('/tkreceipts');
    const changePageKonsolSettings = () => handleNavigation('/konsolsettings');
    const changePageAleSettings = () => handleNavigation('/alesettings');
    const changePagePdfSettings = () => handleNavigation('/pdfsettings');
    const changePageVfxSettings = () => handleNavigation('/vfxsettings');
    const changePageFormat = () => handleNavigation('/format');
    const changePageRender = () => handleNavigation('/render');

    const dailiesItems = [
        { icon: itemIcons['Upload'],        key: '/upload', label: 'Upload',    link: changePageUpload, permission: ['write:metadata'] },
        { icon: itemIcons['Linking'],       key: '/linking',label: 'Linking',   link: changePageLink,   permission: ['update:media'] },
        { icon: itemIcons['Export'],        key: '/export', label: 'Export',    link: changePageExport, permission: ['write:deliverables'] },
    ]
    const assetsItems = [
        { icon: itemIcons['Explore'],       key: '/explore',label: 'Explore',     link: changePageExplore, permission: ['read:media'] },
        { icon: itemIcons['Asset Tracker'], key: '/asset',  label: 'Asset Tracker',link: changePageAssettrack, permission: ['track:media'] },
        { icon: itemIcons['Upload Media Data'], key: '/data', label: 'Upload Media Data',link: changePageMediaData, desktoponly: true, permission: ['write:media'] },
    ]
    const projectItems = [
        { icon: itemIcons['Manage LUT'],    key: '/lut', label: 'Manage LUT', link: changePageLut, desktoponly: true, permission: ['read:illusion'] },
        { icon: itemIcons['Manage EDL'],    key: '/edl', label: 'Manage EDL', link: changePageEdlManage, desktoponly: true, permission: ['read:illusion'] },
        { icon: itemIcons['Sources Configuration'], key: '/sourceconfig', label: 'Sources Configuration', link: changePageSources, desktoponly: true, permission: ['update:source']},
        { icon: itemIcons['Deliverable Configuration'], key: '/deliverableconfig', label: 'Deliverable Configuration', link: changePageConfig, desktoponly: true, permission: ['update:deliverables']},
        { icon: itemIcons['FDL Generate'],  key: '/fdl', label: 'Generate FDL', link: changePageFdl, desktoponly: true, permission: ['read:fdl'] },
    ] 
    const illusionItems = [
        { icon: itemIcons['Illusion'],      key: '/illusion',label: 'Illusion', link: changePageIllusionPulls, permission: ['read:illusion'] },
        // { icon: <Biotech />,        key: '/pipelinelab', label: 'Pipeline Lab', link: changePagePipelineLab },
        // { icon: <ReceiptLong />,    key: '/transkoder',label: 'Receipt Overview', link: changePageTranskoder }
    ]

    useEffect(() => {
    }, [currentPage])
    return (
        <React.Fragment>
            <div className="content">
                <div className={isOffscreen ? "" : "scrollable-container"} style={{ height: '100%' }}>
                    <Box
                        sx={{ 
                            opacity: '1',
                            transform: 'translateX(0%)',
                            transitionProperty: 'opacity, transform',
                            transitionDuration: isOffscreen ? '0.01s' : '0.9s', 
                            transitionTimingFunction: 'ease-out',
                            '&.on': {
                                opacity: '0',
                                transform: 'translateX(-100%)'
                            },
                        }}
                        className={isOffscreen ? 'on' : ''}
                    >
                        <PermissionGuard permission={(userPermissions) => dailiesItems.map(i => i?.permission ?? []).flat().some(perm => userPermissions.includes(perm))}>
                            <SideList
                                currentPage={currentPage}
                                open={dailies}
                                setOpen={dailiesMenu}
                                title="Dailies"
                                subtitle="Manage metadata tools"
                                data={dailiesItems}
                            />
                        </PermissionGuard>
                        <PermissionGuard permission={(userPermissions) => illusionItems.map(i => i?.permission ?? []).flat().some(perm => userPermissions.includes(perm))}>
                            <SideList
                                currentPage={currentPage}
                                open={illusion}
                                setOpen={illusionMenu}
                                title="Illusion"
                                subtitle="Manage VFX Pulls"
                                data={illusionItems}
                            />
                        </PermissionGuard>
                        <PermissionGuard permission={(userPermissions) => assetsItems.map(i => i?.permission ?? []).flat().some(perm => userPermissions.includes(perm))}>
                            <SideList
                                currentPage={currentPage}
                                open={assets}
                                setOpen={assetsMenu}
                                title="Manage Assets"
                                subtitle="Manage and track media"
                                data={assetsItems}
                            />
                        </PermissionGuard>
                        <PermissionGuard permission={(userPermissions) => projectItems.map(i => i?.permission ?? []).flat().some(perm => userPermissions.includes(perm))}>
                            <SideList
                                currentPage={currentPage}
                                open={project}
                                setOpen={projectMenu}
                                title="Project files"
                                subtitle="Manage project related files"
                                data={projectItems}
                            />
                        </PermissionGuard>
                        <PermissionGuard permission={'update:project'}> 
                            <SideSettings
                                title="Settings"
                                subtitle="Configure Konsol"
                                setOpen={changePageSettings}
                            />
                        </PermissionGuard>
                        <PermissionGuard permission={'update:project'}>
                            <SideSettings
                                title="Konsol Config"
                                subtitle="Configure Konsol Services"
                                setOpen={changePageKonsolConfig}
                            />
                        </PermissionGuard>
                    </Box>
                </div>
            </div>

            <ConfirmNavigationDialog
                open={confirmDialogOpen} 
                onClose={handleCancelNavigation} 
                onConfirm={handleConfirmNavigation}
                clickAway={handleCancelNavigation}
            />
        </React.Fragment>
    );
}
